import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../store/useAuthStore';
import { motion, AnimatePresence } from 'framer-motion';
import { db } from '../lib/firebase';
import { doc, setDoc } from 'firebase/firestore';
import { TIMEZONES } from './SettingsPage';

const LocationIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" /></svg>;
const WorkIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M20.25 14.15v4.25c0 1.094-.787 2.036-1.872 2.18-2.087.277-4.216.42-6.378.42s-4.291-.143-6.378-.42c-1.085-.144-1.872-1.086-1.872-2.18v-4.25m16.5 0a2.18 2.18 0 00.75-1.661V8.706c0-1.081-.768-2.015-1.837-2.175a48.114 48.114 0 00-3.413-.387m4.5 8.006c-.194.165-.42.295-.673.38A23.978 23.978 0 0112 15.75c-2.648 0-5.195-.429-7.577-1.22a2.016 2.016 0 01-.673-.38m0 0A2.18 2.18 0 013 12.489V8.706c0-1.081.768-2.015 1.837-2.175a48.111 48.111 0 013.413-.387m7.5 0V5.25A2.25 2.25 0 0013.5 3h-3a2.25 2.25 0 00-2.25 2.25v.894m7.5 0a48.667 48.667 0 00-7.5 0M12 12.75h.008v.008H12v-.008z" /></svg>;
const PersonIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" /></svg>;
const CheckIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>;

const OnboardingPage = () => {
    const navigate = useNavigate();
    const { updateProfile, user } = useAuthStore();
    const [activeStep, setActiveStep] = useState(0);
    const [loading, setLoading] = useState(false);

    const [formData, setFormData] = useState({
        timezone: 'Auto-detect',
        occupation: '',
        bio: ''
    });

    const steps = [
        { label: 'Timezone', icon: <LocationIcon className="w-5 h-5" /> },
        { label: 'Role', icon: <WorkIcon className="w-5 h-5" /> },
        { label: 'Context', icon: <PersonIcon className="w-5 h-5" /> }
    ];

    const handleNext = async () => {
        if (activeStep === steps.length - 1) {
            await handleSubmit();
        } else {
            setActiveStep((prev) => prev + 1);
        }
    };

    const handleBack = () => {
        setActiveStep((prev) => prev - 1);
    };

    const handleSubmit = async () => {
        setLoading(true);
        try {
            // Update root user document for onboarding completion flag
            await updateProfile({
                onboardingCompleted: true
            });

            // Update sub-collection for settings profile consistency
            const profileRef = doc(db, 'users', user.uid, 'settings', 'profile');
            await setDoc(profileRef, {
                displayName: user.displayName || '',
                bio: formData.bio,
                occupation: formData.occupation,
                timezone: formData.timezone,
                avatarUrl: user.photoURL || ''
            }, { merge: true });

            window.location.href = '/';
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (field) => (e) => {
        setFormData({ ...formData, [field]: e.target.value });
    };

    const isStepValid = () => {
        if (activeStep === 0) return formData.timezone.length > 0;
        if (activeStep === 1) return formData.occupation.trim().length > 1;
        if (activeStep === 2) return formData.bio.trim().length > 5;
        return false;
    };

    return (
        <div className="min-h-screen bg-[var(--bg-main)] text-[var(--text-main)] font-sans flex flex-col items-center justify-center relative overflow-hidden transition-colors duration-300">
            {/* Background Aesthetic Elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <motion.div
                    animate={{
                        scale: [1, 1.2, 1],
                        opacity: [0.3, 0.5, 0.3],
                        rotate: [0, 90, 0]
                    }}
                    transition={{
                        duration: 20,
                        repeat: Infinity,
                        ease: "linear"
                    }}
                    className="absolute -top-[10%] -right-[10%] w-[50%] h-[50%] rounded-full bg-[var(--accent-primary)]/20 blur-[120px]"
                />
                <motion.div
                    animate={{
                        scale: [1, 1.1, 1],
                        opacity: [0.2, 0.4, 0.2],
                        rotate: [0, -45, 0]
                    }}
                    transition={{
                        duration: 15,
                        repeat: Infinity,
                        ease: "linear"
                    }}
                    className="absolute -bottom-[10%] -left-[10%] w-[45%] h-[45%] rounded-full bg-indigo-500/15 blur-[100px]"
                />
            </div>

            <div className="w-full max-w-md px-6 relative z-10">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-[var(--bg-secondary)]/40 backdrop-blur-3xl border border-white/5 rounded-[2rem] p-8 sm:p-10 shadow-2xl relative overflow-hidden"
                >
                    <div className="text-center mb-10 relative z-10">
                        <h1 className="text-3xl font-light text-[var(--text-main)] mb-2 tracking-tight">
                            Welcome, <span className="text-[var(--accent-primary)] font-medium">{user?.displayName?.split(' ')[0] || 'Friend'}</span>.
                        </h1>
                        <p className="text-[var(--text-muted)] text-[13px] font-medium tracking-tight opacity-60">Let's craft your personalized AI environment.</p>
                    </div>

                    {/* Stepper Redesign - Compact */}
                    <div className="flex items-center justify-center w-full mb-12 relative z-10 px-8">
                        {steps.map((step, index) => {
                            const isActive = index === activeStep;
                            const isCompleted = index < activeStep;
                            return (
                                <div key={step.label} className="flex items-center flex-1 last:flex-none">
                                    <div className="flex flex-col items-center relative group">
                                        <motion.div
                                            animate={{
                                                scale: isActive ? 1.1 : 1,
                                                borderColor: isActive ? "rgba(255, 255, 255, 0.2)" : isCompleted ? "rgba(16, 185, 129, 0.3)" : "rgba(255, 255, 255, 0.05)",
                                                backgroundColor: isActive ? "rgba(255, 255, 255, 0.05)" : isCompleted ? "rgba(16, 185, 129, 0.05)" : "transparent"
                                            }}
                                            className={`flex items-center justify-center w-10 h-10 rounded-xl border transition-all duration-500 z-10 bg-[var(--bg-tertiary)]/10 backdrop-blur-sm shadow-xl`}
                                        >
                                            {isCompleted ? (
                                                <CheckIcon className="w-5 h-5 text-emerald-500 opacity-60" />
                                            ) : (
                                                <div className={`transition-colors duration-300 ${isActive ? 'text-[var(--text-main)]' : 'text-[var(--text-muted)] opacity-20'}`}>
                                                    <div className="scale-90">{step.icon}</div>
                                                </div>
                                            )}
                                        </motion.div>
                                        <span className={`absolute -bottom-6 text-[8px] font-black uppercase tracking-[0.2em] whitespace-nowrap transition-all duration-300 ${isActive ? 'text-[var(--text-main)] opacity-100' : isCompleted ? 'text-emerald-500 opacity-50' : 'text-[var(--text-muted)] opacity-20'}`}>
                                            {step.label}
                                        </span>
                                    </div>
                                    {index < steps.length - 1 && (
                                        <div className="flex-1 mx-3 h-px rounded-full overflow-hidden bg-white/5">
                                            <motion.div
                                                initial={{ width: "0%" }}
                                                animate={{ width: isCompleted ? "100%" : "0%" }}
                                                className="h-full bg-emerald-500/20"
                                            />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    <div className="min-h-[180px] flex flex-col justify-center relative z-10 mt-4">
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={activeStep}
                                initial={{ opacity: 0, x: 10 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -10 }}
                                transition={{ duration: 0.3 }}
                                className="text-center"
                            >
                                {activeStep === 0 && (
                                    <>
                                        <h2 className="text-xl font-light text-[var(--text-main)] mb-2 tracking-tight">Timezone selection</h2>
                                        <p className="text-[var(--text-muted)] text-[11px] font-medium mb-6 tracking-tight opacity-40 uppercase tracking-[0.1em]">Helps me sync with your schedule.</p>
                                        <div className="relative max-w-[280px] mx-auto">
                                            <select
                                                autoFocus
                                                value={formData.timezone}
                                                onChange={handleChange('timezone')}
                                                className="w-full bg-[#1A1918] border border-white/10 focus:border-white/20 focus:ring-4 focus:ring-white/5 rounded-xl px-4 py-3 text-[var(--text-main)] outline-none text-center transition-all text-base font-light tracking-tight appearance-none cursor-pointer"
                                            >
                                                {TIMEZONES.map(tz => <option key={tz} value={tz} className="bg-[#1A1918] text-[var(--text-main)]">{tz}</option>)}
                                            </select>
                                            <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none opacity-20">
                                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                            </div>
                                        </div>
                                    </>
                                )}
                                {activeStep === 1 && (
                                    <>
                                        <h2 className="text-xl font-light text-[var(--text-main)] mb-2 tracking-tight">What keeps you busy?</h2>
                                        <p className="text-[var(--text-muted)] text-[11px] font-medium mb-6 tracking-tight opacity-40 uppercase tracking-[0.1em]">Helps the AI match your tone and needs.</p>
                                        <input
                                            type="text"
                                            autoFocus
                                            placeholder="Occupation (e.g. Developer, Student, Lead)"
                                            value={formData.occupation}
                                            onChange={handleChange('occupation')}
                                            className="w-full max-w-[280px] mx-auto bg-[#1A1918] border border-white/10 focus:border-white/20 focus:ring-4 focus:ring-white/5 rounded-xl px-4 py-3 text-[var(--text-main)] outline-none text-center transition-all text-base font-light tracking-tight placeholder:opacity-20"
                                        />
                                    </>
                                )}
                                {activeStep === 2 && (
                                    <>
                                        <h2 className="text-xl font-light text-[var(--text-main)] mb-2 tracking-tight">Personalized Context</h2>
                                        <p className="text-[var(--text-muted)] text-[11px] font-medium mb-6 tracking-tight opacity-40 uppercase tracking-[0.1em]">Brief profile so AI experts can know you better.</p>
                                        <textarea
                                            rows={4}
                                            autoFocus
                                            placeholder="e.g. I am a developer. I enjoy hiking..."
                                            value={formData.bio}
                                            onChange={handleChange('bio')}
                                            className="w-full bg-[#1A1918] border border-white/10 focus:border-white/20 focus:ring-4 focus:ring-white/5 rounded-xl px-5 py-4 text-[var(--text-main)] outline-none transition-all text-[14px] font-light tracking-tight leading-relaxed min-h-[120px] resize-none placeholder:opacity-20 custom-scrollbar"
                                        />
                                    </>
                                )}
                            </motion.div>
                        </AnimatePresence>
                    </div>

                    {/* Bottom Nav */}
                    <div className="flex justify-between items-center mt-8 pt-6 border-t border-white/5 relative z-10 gap-4">
                        <button
                            disabled={activeStep === 0 || loading}
                            onClick={handleBack}
                            className="px-5 py-2 text-[11px] font-bold uppercase tracking-[0.2em] text-[var(--text-muted)] hover:text-[var(--text-main)] disabled:opacity-0 transition-all rounded-full hover:bg-white/5"
                        >
                            Back
                        </button>
                        <button
                            onClick={handleNext}
                            disabled={!isStepValid() || loading}
                            className="flex-1 max-w-[160px] px-5 py-2.5 bg-[var(--text-main)] hover:bg-[var(--text-main)]/90 disabled:bg-white/5 disabled:text-[var(--text-muted)]/20 text-[#1A1918] text-[12px] font-bold rounded-full shadow-lg transition-all text-center flex items-center justify-center gap-2 active:scale-[0.98] group overflow-hidden relative"
                        >
                            {loading ? (
                                <svg className="animate-spin w-3.5 h-3.5 opacity-70" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                            ) : null}
                            <span className="relative z-10 truncate">
                                {activeStep === steps.length - 1 ? (loading ? 'Setting up...' : 'Finish') : 'Continue'}
                            </span>
                        </button>
                    </div>
                </motion.div>

                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 0.2 }}
                    className="text-center mt-8 text-[9px] font-bold text-[var(--text-muted)] uppercase tracking-[0.3em]"
                >
                    Intrellian • {new Date().getFullYear()}
                </motion.p>
            </div>
        </div>
    );
};

export default OnboardingPage;
